import { AxiosResponse } from 'axios';

import apiClient from './client';
import i18n from 'i18n';
import { ApiResponseDto } from 'interfaces/apiResponse';
import { PageResultDto } from 'interfaces/page';
import {
  CreateErganiSettings,
  ErganiBranchAssociation,
  ErganiBranches,
  ErganiEmployeeDto,
  ErganiEmployeesQueryDto,
  ErganiEmployerInfo,
  ErganiScheduleOverrideResponse,
  ErganiScheduleCheckResponse,
  ErganiScheduleDeleteResponse,
  ErganiWeeklyScheduleCheckResponse,
  ErganiWeeklyScheduleResponse,
} from 'interfaces/ergani';

/**
 * HTTP status the backend uses when the ERGANI system itself rejected or failed the request
 * (see `ErganiException` -> `Status.BAD_GATEWAY`).
 */
const ERGANI_ERROR_STATUS = 502;

/**
 * Error raised when ERGANI rejected a request. ERGANI has no stable error codes, so the message
 * coming from the backend is the only meaningful thing we can show to the user.
 */
export class ErganiApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ErganiApiError';
  }
}

export const isErganiApiError = (error: unknown): error is ErganiApiError => error instanceof ErganiApiError;

/**
 * Returns the ERGANI-provided message when the error originated from ERGANI, otherwise the fallback.
 */
export const getErganiErrorMessage = (error: unknown, fallback: string): string =>
  isErganiApiError(error) && error.message ? error.message : fallback;

const joinApiErrors = (errors: ApiResponseDto<unknown>['errors']): string | undefined => {
  const message = (errors ?? [])
    .map(error => error.message)
    .filter(Boolean)
    .join(', ');

  return message || undefined;
};

interface HttpErrorLike {
  response?: { status?: number; data?: ApiResponseDto<unknown> };
}

/**
 * Re-throws request errors so that ERGANI failures (502) surface with the backend message.
 */
const rethrowErganiError = (error: unknown): never => {
  const response = (error as HttpErrorLike | undefined)?.response;

  if (response?.status === ERGANI_ERROR_STATUS) {
    const message = joinApiErrors(response.data?.errors);

    if (message) {
      throw new ErganiApiError(message, response.data?.errors?.[0]?.code);
    }
  }

  throw error;
};

/**
 * Executes a request against an ERGANI-related endpoint and unwraps the `ApiResponseDto`.
 */
const erganiRequest = async <T>(request: () => Promise<AxiosResponse<ApiResponseDto<T>>>): Promise<T> => {
  let response: AxiosResponse<ApiResponseDto<T>>;

  try {
    response = await request();
  } catch (error) {
    return rethrowErganiError(error);
  }

  if (response.data.errors) {
    throw new Error(joinApiErrors(response.data.errors));
  }

  return response.data.data as T;
};

interface ErganiService {
  getUsername(): Promise<string | null>;
  upsertCredentials(postData: CreateErganiSettings): Promise<void>;
  getEmployer(): Promise<ErganiEmployerInfo>;
  getBranches(): Promise<ErganiBranches>;
  searchEmployees(queryParams: ErganiEmployeesQueryDto): Promise<PageResultDto<ErganiEmployeeDto>>;
  getAssociations(): Promise<ErganiBranchAssociation[]>;
  upsertAssociation(association: ErganiBranchAssociation): Promise<ErganiBranchAssociation>;
  deleteAssociation(serialNumber: string): Promise<void>;
  sendScheduleOverride(overrideId: string): Promise<ErganiScheduleOverrideResponse>;
  checkScheduleOverride(overrideId: string, signal?: AbortSignal): Promise<ErganiScheduleCheckResponse>;
  deleteScheduleOverride(overrideId: string): Promise<ErganiScheduleDeleteResponse>;
  checkWeeklySchedule(scheduleId: number): Promise<ErganiWeeklyScheduleCheckResponse>;
  sendWeeklySchedule(scheduleId: number): Promise<ErganiWeeklyScheduleResponse>;
}

const endpoints = {
  getUsername: '/ergani/authenticate/username',
  upsertCredentials: '/ergani/authenticate',
  employer: '/ergani/employer',
  branches: '/ergani/branches',
  associations: '/ergani/branches/associations',
  association: (serialNumber: string) => `/ergani/branches/associations/${encodeURIComponent(serialNumber)}`,
  employees: '/ergani/employees',
  scheduleOverride: (id: string) => `/ergani/schedule/overrides/${id}`,
  scheduleOverrideCheck: (id: string) => `/ergani/schedule/overrides/${id}/check`,
  weeklySchedule: (id: number) => `/ergani/schedule/${id}`,
  weeklyScheduleCheck: (id: number) => `/ergani/schedule/${id}/check`,
};

export const erganiServices: ErganiService = {
  getUsername: async (): Promise<string | null> => {
    try {
      const username = await erganiRequest<string>(() => apiClient.get(endpoints.getUsername));
      return username || null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  upsertCredentials: async (postData: CreateErganiSettings): Promise<void> => {
    try {
      await erganiRequest<void>(() => apiClient.put(endpoints.upsertCredentials, postData));
    } catch (error: any) {
      if (isErganiApiError(error)) {
        throw error;
      }

      if (error.response?.status === 400) {
        throw new Error(i18n.t('apiErrors.erganiInvalidCredentials'));
      }

      if (error.response?.status === ERGANI_ERROR_STATUS) {
        throw new Error(i18n.t('apiErrors.erganiUnavailable'));
      }

      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors[0].message);
      }

      throw error;
    }
  },

  getEmployer: (): Promise<ErganiEmployerInfo> => erganiRequest(() => apiClient.get(endpoints.employer)),

  getBranches: (): Promise<ErganiBranches> => erganiRequest(() => apiClient.get(endpoints.branches)),

  searchEmployees: (queryParams: ErganiEmployeesQueryDto): Promise<PageResultDto<ErganiEmployeeDto>> =>
    erganiRequest(() => apiClient.get(endpoints.employees, { params: { ...queryParams } })),

  getAssociations: async (): Promise<ErganiBranchAssociation[]> => {
    const response: AxiosResponse<ApiResponseDto<ErganiBranchAssociation[]>> = await apiClient.get(endpoints.associations);

    if (response.data.errors) {
      throw new Error(response.data.errors!.map(error => error.message).join(', '));
    }

    return response.data.data ?? [];
  },

  upsertAssociation: async (association: ErganiBranchAssociation): Promise<ErganiBranchAssociation> => {
    try {
      const response: AxiosResponse<ApiResponseDto<ErganiBranchAssociation>> = await apiClient.put(endpoints.associations, association);

      if (response.data.errors) {
        throw new Error(response.data.errors.map(error => error.message).join(', '));
      }

      return response.data.data!;
    } catch (error: any) {
      if (error.response?.status === 409) {
        throw new Error(i18n.t('apiErrors.erganiBranchAlreadyAssociated'));
      }

      if (error.response?.data?.errors) {
        throw new Error(error.response.data.errors[0].message);
      }

      throw error;
    }
  },

  deleteAssociation: async (serialNumber: string): Promise<void> => {
    const response: AxiosResponse<ApiResponseDto<void>> = await apiClient.delete(endpoints.association(serialNumber));

    if (response.data?.errors) {
      throw new Error(response.data.errors.map(error => error.message).join(', '));
    }
  },

  sendScheduleOverride: (overrideId: string): Promise<ErganiScheduleOverrideResponse> =>
    erganiRequest(() => apiClient.post(endpoints.scheduleOverride(overrideId))),

  checkScheduleOverride: (overrideId: string, signal?: AbortSignal): Promise<ErganiScheduleCheckResponse> =>
    erganiRequest(() => apiClient.get(endpoints.scheduleOverrideCheck(overrideId), { signal })),

  deleteScheduleOverride: (overrideId: string): Promise<ErganiScheduleDeleteResponse> =>
    erganiRequest(() => apiClient.delete(endpoints.scheduleOverride(overrideId))),

  checkWeeklySchedule: (scheduleId: number): Promise<ErganiWeeklyScheduleCheckResponse> =>
    erganiRequest(() => apiClient.get(endpoints.weeklyScheduleCheck(scheduleId))),

  sendWeeklySchedule: (scheduleId: number): Promise<ErganiWeeklyScheduleResponse> =>
    erganiRequest(() => apiClient.post(endpoints.weeklySchedule(scheduleId))),
};
